import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  HelpCircle, 
  Library, 
  ArrowRight,
  Clock,
  Sparkles,
  Users,
  Zap,
  Crown,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchUsage();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/subscription/usage`, {
        headers: getAuthHeader()
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min(100, (used / limit) * 100);
  };

  const getUsageColor = (used, limit) => {
    if (limit === -1) return 'bg-emerald-500';
    const percentage = (used / limit) * 100;
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground text-lg">
            Ready to create your next quiz?
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border-border">
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-20 mb-3" />
                    <Skeleton className="h-8 w-12" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="border-border hover:border-accent/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        <Library className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">Total Quizzes</span>
                    </div>
                    <p className="text-3xl font-serif font-medium text-primary" data-testid="stat-quizzes">
                      {stats?.total_quizzes || 0}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="border-border hover:border-accent/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">Total Lessons</span>
                    </div>
                    <p className="text-3xl font-serif font-medium text-primary" data-testid="stat-lessons">
                      {stats?.total_lessons || 0}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="border-border hover:border-accent/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        <HelpCircle className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">Total Questions</span>
                    </div>
                    <p className="text-3xl font-serif font-medium text-primary" data-testid="stat-questions">
                      {stats?.total_questions || 0}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          )}
        </div>

        {/* Usage Stats Card */}
        {usage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-10"
          >
            <Card className="border-border" data-testid="usage-stats-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Your Usage
                  </CardTitle>
                  <Badge variant="secondary" className="capitalize">
                    {usage.plan_name} Plan
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Quizzes Usage */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Quizzes this month
                      </span>
                      <span className="font-medium" data-testid="quiz-usage">
                        {usage.quizzes_used} / {usage.quizzes_limit === -1 ? '∞' : usage.quizzes_limit}
                        {usage.bonus_credits > 0 && (
                          <span className="text-emerald-600 ml-1">+{usage.bonus_credits} bonus</span>
                        )}
                      </span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(usage.quizzes_used, usage.quizzes_limit)} 
                      className="h-2"
                    />
                    {usage.quizzes_limit !== -1 && usage.quizzes_remaining <= 2 && usage.quizzes_remaining > 0 && usage.bonus_credits === 0 && (
                      <p className="text-xs text-amber-600">
                        Only {usage.quizzes_remaining} quiz{usage.quizzes_remaining > 1 ? 'es' : ''} remaining this month
                      </p>
                    )}
                    {usage.quizzes_remaining === 0 && usage.quizzes_limit !== -1 && (
                      <p className="text-xs text-red-600">
                        Quiz limit reached. Upgrade to Unlimited to keep creating!
                      </p>
                    )}
                  </div>

                  {/* Students Usage */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Students
                      </span>
                      <span className="font-medium" data-testid="student-usage">
                        {usage.students_used} / {usage.students_limit === -1 ? '∞' : usage.students_limit}
                      </span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(usage.students_used, usage.students_limit)} 
                      className="h-2"
                    />
                    {usage.students_limit !== -1 && usage.students_remaining === 0 && (
                      <p className="text-xs text-red-600">
                        Student limit reached. Upgrade to add more!
                      </p>
                    )}
                  </div>
                </div>

                {/* Upgrade option */}
                {usage.quizzes_limit !== -1 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground">
                        Need more? Get unlimited quizzes &amp; students for $27.99/month.
                      </p>
                      <Button 
                        size="sm" 
                        onClick={() => navigate('/pricing')}
                        className="gap-1"
                        data-testid="upgrade-btn"
                      >
                        <Crown className="w-3 h-3" />
                        Upgrade to Unlimited
                      </Button>
                    </div>
                  </div>
                )}

                {/* Reset date info */}
                <p className="text-xs text-muted-foreground mt-4">
                  Monthly quota resets on {new Date(usage.reset_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {usage.bonus_credits > 0 && ' • Bonus credits never expire'}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Main Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Create New Quiz Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card 
              className="border-border hover:border-accent/40 hover:shadow-lg transition-all cursor-pointer group h-full"
              onClick={() => navigate('/create')}
              data-testid="create-quiz-card"
            >
              <CardContent className="p-8 flex flex-col h-full">
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <Plus className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-primary mb-3">
                  Create New Quiz
                </h3>
                <p className="text-muted-foreground mb-6 flex-grow">
                  Upload a lesson, select your question types, and let AI generate a comprehensive quiz with explanations.
                </p>
                <Button className="w-full bg-primary hover:bg-primary/90 gap-2" data-testid="create-quiz-btn">
                  <Sparkles className="w-4 h-4" />
                  Start Creating
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Stats / Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="border-border h-full">
              <CardHeader className="pb-4">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  Recent Quizzes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="w-10 h-10 rounded-lg" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-32 mb-2" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : stats?.recent_quizzes?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_quizzes.map((quiz, index) => (
                      <Link
                        key={quiz.id}
                        to={`/quiz/${quiz.id}`}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/50 transition-colors group"
                        data-testid={`recent-quiz-${index}`}
                      >
                        <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                            {quiz.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {quiz.subject} • {quiz.grade_level}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(quiz.updated_at)}
                        </span>
                      </Link>
                    ))}
                    <Link
                      to="/library"
                      className="flex items-center justify-center gap-2 p-3 text-primary hover:bg-secondary/50 rounded-lg transition-colors"
                      data-testid="view-all-quizzes"
                    >
                      View all quizzes
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Library className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-4">No quizzes yet</p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/create')}
                      className="gap-2"
                      data-testid="empty-create-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create your first quiz
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="border-border bg-secondary/30">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-lg font-medium text-primary mb-1">
                    Need to review or edit existing quizzes?
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Access your full quiz library to edit, duplicate, or export any quiz.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/library')}
                  className="gap-2 whitespace-nowrap"
                  data-testid="go-to-library-btn"
                >
                  <Library className="w-4 h-4" />
                  Go to Library
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default DashboardPage;
